import { Avatar, Box, ButtonBase, makeStyles, Typography } from "@material-ui/core";
import { darken, fade } from "@material-ui/core/styles";
import dayjs from "dayjs";
import React from "react";

import { StrengthTags } from "@/components/agent/PlannerTags";
import { StatusBadge } from "@/components/Badge";
import { PLANNER_STRENGTHS_LABEL } from "@/constants/plannerProfile";
import { layoutTokens, semanticColors as colors, textStyles } from "@/designTokens";
import theme from "@/theme";

import NoImagePath from "@assets/image/NoImage.svg";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "left",
    width: "100%",
    padding: layoutTokens.space[16],
    gap: layoutTokens.space[8],
    background: colors["surface/card"],
    borderRadius: layoutTokens.radius.lg,
    boxShadow: `0 2px 8px ${fade(colors["text/primary"], 0.06)}`,
    [theme.breakpoints.up("md")]: {
      padding: "16px 20px",
      borderRadius: 0,
      boxShadow: "none",
      borderBottom: `1px solid ${colors["border/default"]}`,
    },
  },
  selected: { background: colors["state/selected-bg"] },
  who: { display: "flex", alignItems: "center", gap: layoutTokens.space[12] },
  avatar: { width: 44, height: 44, [theme.breakpoints.up("md")]: { width: 40, height: 40 } },
  readAvatar: { opacity: 0.72 },
  names: { flex: 1, minWidth: 0 },
  nameRow: { display: "flex", gap: layoutTokens.space[8], alignItems: "center" },
  name: { ...textStyles["SP/Heading/H3"], flex: 1, minWidth: 0, overflowWrap: "anywhere" },
  readName: { ...textStyles["SP/Body/XLarge"], color: colors["text/secondary"] },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: layoutTokens.space[8],
    flexShrink: 0,
    flexWrap: "wrap",
    justifyContent: "flex-end",
    maxWidth: "50%",
  },
  date: { ...textStyles["Number/Caption"], color: colors["text/secondary"] },
  labels: { display: "flex", gap: layoutTokens.space[8], flexWrap: "wrap", alignItems: "center" },
  badge: { "&&": { fontSize: 12, height: 24 } },
  todayBadge: { "&&": { backgroundColor: colors["status/info"], color: colors["text/inverse"] } },
  unreadBadge: { "&&": { backgroundColor: darken(colors["accent/mark"], 0.26), color: colors["text/inverse"] } },
  fieldLabel: { ...textStyles["SP/Body/Small"], color: colors["text/heading"] },
  company: {
    ...textStyles["SP/Body/Small"],
    overflowWrap: "anywhere",
    color: colors["text/secondary"],
    marginTop: layoutTokens.space[4],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  subject: ({ isRead }: { isRead?: boolean }) => ({
    ...textStyles["SP/Body/Small"],
    fontWeight: isRead ? 400 : 700,
    color: colors["text/secondary"],
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"], fontWeight: isRead ? 400 : 700 },
  }),
});

interface MessageCardProps {
  name: string;
  company?: string;
  image?: string;
  subject?: string | null;
  date?: string;
  isRead?: boolean;
  isMatched?: boolean;
  selected?: boolean;
  arrivalLabel?: string;
  strengthLabels?: string[];
  onClick: () => void;
}
export const MessageCard: React.FC<MessageCardProps> = ({
  name,
  company,
  image,
  subject,
  date,
  isRead,
  isMatched,
  selected,
  arrivalLabel,
  strengthLabels = [],
  onClick,
}) => {
  const classes = useStyles({ isRead });
  return (
    <ButtonBase
      className={`${classes.root} ${selected ? classes.selected : ""}`}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`${name}のメッセージ`}
      data-testid="message-card"
    >
      <Box className={classes.labels}>
        {arrivalLabel && (
          <StatusBadge
            label={arrivalLabel}
            status="info"
            className={`${classes.badge} ${arrivalLabel === "本日届いたメッセージ" ? classes.todayBadge : ""}`}
          />
        )}
        {!isRead && <StatusBadge label="未読" status="warning" className={`${classes.badge} ${classes.unreadBadge}`} />}
      </Box>
      <Box className={classes.who}>
        <Avatar src={image || NoImagePath} alt="" className={`${classes.avatar} ${isRead ? classes.readAvatar : ""}`} />
        <Box className={classes.names}>
          <Box className={classes.nameRow}>
            <Typography className={`${classes.name} ${isRead ? classes.readName : ""}`}>{name}</Typography>
            <Box className={classes.meta}>
              {isMatched && <StatusBadge label="マッチ済" status="success" className={classes.badge} />}
              <span className={classes.date}>{date && dayjs(date).isValid() ? dayjs(date).format("M/D") : ""}</span>
            </Box>
          </Box>
          <Typography className={classes.company}>{company}</Typography>
        </Box>
      </Box>
      {subject && (
        <Typography className={classes.subject} data-testid="message-subject">{subject}</Typography>
      )}
      {strengthLabels.length > 0 && (
        <>
          <Typography className={classes.fieldLabel}>{PLANNER_STRENGTHS_LABEL}</Typography>
          <StrengthTags labels={strengthLabels} />
        </>
      )}
    </ButtonBase>
  );
};
